/**
 * Cookie Consent Manager
 * Handles user consent for analytics and caching cookies
 * Compliant with GDPR, CCPA, and other privacy regulations
 */

class CookieConsentManager {
  constructor() {
    this.consentKey = 'prepxai_cookie_consent';
    this.preferences = this.loadPreferences();
  }

  /**
   * Load consent preferences from localStorage
   */
  loadPreferences() {
    const stored = localStorage.getItem(this.consentKey);
    if (stored) {
      return JSON.parse(stored);
    }

    return {
      necessary: true, // Always required
      analytics: false,
      caching: false, // For performance caching
      marketing: false,
      accepted: false,
      timestamp: null,
    };
  }

  /**
   * Save consent preferences
   */
  savePreferences(preferences) {
    const updated = {
      ...this.preferences,
      ...preferences,
      timestamp: Date.now(),
      accepted: true,
    };
    localStorage.setItem(this.consentKey, JSON.stringify(updated));
    this.preferences = updated;
    return updated;
  }

  /**
   * Check if specific consent is given
   */
  hasConsent(type) {
    return this.preferences[type] === true;
  }

  /**
   * Accept all cookies
   */
  acceptAll() {
    return this.savePreferences({
      necessary: true,
      analytics: true,
      caching: true,
      marketing: true,
    });
  }

  /**
   * Accept only necessary
   */
  acceptNecessary() {
    return this.savePreferences({
      necessary: true,
      analytics: false,
      caching: false,
      marketing: false,
    });
  }

  /**
   * Check if consent banner was shown
   */
  isConsentGiven() {
    return this.preferences.accepted === true;
  }

  /**
   * Get consent status
   */
  getPreferences() {
    return {
      ...this.preferences,
      isGiven: this.isConsentGiven(),
    };
  }

  /**
   * Reset consent (useful for privacy/settings pages)
   */
  resetConsent() {
    localStorage.removeItem(this.consentKey);
    this.preferences = this.loadPreferences();
  }
}

// Singleton instance
let consentManager = null;

export const getCookieConsentManager = () => {
  if (!consentManager) {
    consentManager = new CookieConsentManager();
  }
  return consentManager;
};

export default CookieConsentManager;
